import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader, StatusChip, UserNameCell } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { ASSESSMENT_TYPES, REVIEW_TYPES } from '@/modules/shared/hr-form-engine';
import { formatOrgDateTime } from '@/lib/utils/custom-date/custom-date';
import { formatDate, isDeadlineMissed } from '@/modules/performance/hr-review-core';
import { SubmissionRow } from '../../types/submission.types';

const STATUSES = ['Pending', 'Completed', 'Transferred'];

interface CreateSubmissionsTableColumnsProps {
  t: (key: string) => string;
  departmentNames: Map<string, string>;
  designationNames: Map<string, string>;
  /** The employee's email by IAM user id, shown under their name. */
  emailOf?: (userId: string) => string | undefined;
  onOpen: (row: SubmissionRow) => void;
}

export const createSubmissionsTableColumns = ({
  t,
  departmentNames,
  designationNames,
  emailOf,
  onOpen,
}: CreateSubmissionsTableColumnsProps): ColumnDef<SubmissionRow>[] => [
  {
    id: 'Name',
    // Filtered by the toolbar's name search.
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_NAME')} />,
    accessorFn: (row) => row.detail.Name,
    cell: ({ row }) => (
      <UserNameCell
        name={row.original.detail.Name}
        email={emailOf?.(row.original.review?.ReviewForId ?? '')}
      />
    ),
  },
  {
    id: 'Status',
    meta: {
      filter: {
        type: 'options',
        options: STATUSES.map((status) => ({ value: status, label: t(status.toUpperCase()) })),
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
    accessorFn: (row) => row.detail.Status,
    cell: ({ row }) => (
      <StatusChip status={row.original.detail.Status}>
        {t(row.original.detail.Status.toUpperCase())}
      </StatusChip>
    ),
  },
  {
    id: 'Department',
    // Looked up from the review's department id; filtered by the toolbar.
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEPARTMENT')} />,
    accessorFn: (row) => departmentNames.get(row.review?.DepartmentId ?? '') ?? '-',
  },
  {
    id: 'Designation',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DESIGNATION')} />,
    accessorFn: (row) => designationNames.get(row.review?.DesignationId ?? '') ?? '-',
  },
  {
    id: 'AssessmentType',
    meta: {
      filter: {
        type: 'options',
        options: ASSESSMENT_TYPES.map((type) => ({ value: type.key, label: t(type.title) })),
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('ASSESSMENT_TYPE')} />,
    accessorFn: (row) =>
      t(ASSESSMENT_TYPES.find((type) => type.key === row.detail.AssessmentType)?.title ?? '-'),
  },
  {
    id: 'ReviewType',
    meta: {
      filter: {
        type: 'options',
        options: REVIEW_TYPES.map((type) => ({ value: type.key, label: t(type.title) })),
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('REVIEW_TYPE')} />,
    accessorFn: (row) =>
      t(REVIEW_TYPES.find((type) => type.key === row.detail.ReviewType)?.title ?? '-'),
  },
  {
    id: 'RequestedDateUtc',
    meta: { filter: { type: 'datetime' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('REQUEST_DATE')} />,
    accessorFn: (row) =>
      formatOrgDateTime(
        row.detail.RequestedDateUtc ?? row.detail.CreatedDate,
        row.detail.Timezone || undefined
      ) || '-',
  },
  {
    id: 'PeersDeadLine',
    meta: { filter: { type: 'date' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEADLINE')} />,
    accessorFn: (row) => formatDate(row.detail.PeersDeadLine),
    cell: ({ row }) => {
      const { PeersDeadLine, Status } = row.original.detail;
      const missed = Status === 'Pending' && isDeadlineMissed(PeersDeadLine);
      return <span className={missed ? 'text-error' : undefined}>{formatDate(PeersDeadLine)}</span>;
    },
  },
  {
    id: 'actions',
    // Card: top right, as the old portal's card header.
    meta: { mobile: 'badge' as const },
    header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => (
      <Button variant="outline" size="sm" onClick={() => onOpen(row.original)}>
        {row.original.detail.Status === 'Completed' ? t('VIEW') : t('FILL_OUT')}
      </Button>
    ),
  },
];
